import React, { useEffect, useState } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Space,
  message,
} from 'antd'
import { ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useModulePermission } from '@/hooks/useModulePermission'
import { leadStore } from '../Lead/leadStore'
import { proposalStore } from '../Proposal/proposalStore'
import { LeadStage, ProposalStatus } from '@/types'
import '../Lead/LeadList.css'

interface MonthRow {
  key: string
  month: string
  totalLeads: number
  converted: number
  rate: number
  revenue: number
}

// A won lead's estimated annual revenue = its proposal's rate × annual volume
// — the only "deal size" figure a proposal actually carries (proposals price
// by ₹/kWh, not a lump sum). Same source ProposalDetail.tsx's own template
// interpolation uses (pricingDetails[0].rate, falling back to the legacy
// top-level tariff fields for older proposals saved before pricingDetails
// existed). Prefers the WON proposal for the lead, falling back to an
// APPROVED one if it was won without a formally-won revision on record.
const bestProposalFor = (leadId: string) => {
  const candidates = proposalStore.getAll().filter((p) => p.leadId === leadId)
  return (
    candidates.find((p) => p.status === ProposalStatus.WON) ||
    candidates.find((p) => p.status === ProposalStatus.APPROVED) ||
    candidates[0]
  )
}

const estimatedAnnualRevenue = (leadId: string): number => {
  const p = bestProposalFor(leadId)
  if (!p) return 0
  const pricing = p.pricingDetails?.[0]
  const rate = pricing?.rate ?? p.sakthiTariff ?? p.deliveredTariff ?? p.tariff ?? 0
  const annualVolume = Number(pricing?.volumePerAnnum) || (p.monthlyConsumption ?? 0) * 12
  return rate * annualVolume
}

const buildMonthlyBreakdown = (): MonthRow[] => {
  const buckets = new Map<string, MonthRow>()
  leadStore.getLeads().forEach((lead) => {
    const monthKey = dayjs(lead.createdAt).format('YYYY-MM')
    if (!buckets.has(monthKey)) {
      buckets.set(monthKey, {
        key: monthKey,
        month: dayjs(lead.createdAt).format('MMMM YYYY'),
        totalLeads: 0,
        converted: 0,
        rate: 0,
        revenue: 0,
      })
    }
    const row = buckets.get(monthKey)!
    row.totalLeads += 1
    if (lead.stage === LeadStage.WON) {
      row.converted += 1
      row.revenue += estimatedAnnualRevenue(lead.id)
    }
  })
  return Array.from(buckets.values())
    .map((r) => ({ ...r, rate: r.totalLeads ? Math.round((r.converted / r.totalLeads) * 100) : 0 }))
    .sort((a, b) => (a.key < b.key ? 1 : -1))
}

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`

const LeadConversionReport: React.FC = () => {
  const { canDownload } = useModulePermission('Reports')
  const [reportData, setReportData] = useState<MonthRow[]>(() => buildMonthlyBreakdown())

  useEffect(() => {
    const sync = () => setReportData(buildMonthlyBreakdown())
    sync()
    Promise.all([leadStore.refresh(), proposalStore.refresh()]).then(sync)
    const unsubLeads = leadStore.subscribe(sync)
    window.addEventListener(proposalStore.eventName, sync)
    return () => {
      unsubLeads()
      window.removeEventListener(proposalStore.eventName, sync)
    }
  }, [])

  const totalLeads = reportData.reduce((s, r) => s + r.totalLeads, 0)
  const totalConversions = reportData.reduce((s, r) => s + r.converted, 0)
  const totalRevenue = reportData.reduce((s, r) => s + r.revenue, 0)
  const avgConversionRate = totalLeads ? Math.round((totalConversions / totalLeads) * 100) : 0
  const avgRevenuePerLead = totalConversions ? Math.round(totalRevenue / totalConversions) : 0

  const columns = [
    { title: 'Month', dataIndex: 'month', key: 'month' },
    { title: 'Total Leads', dataIndex: 'totalLeads', key: 'totalLeads' },
    { title: 'Converted', dataIndex: 'converted', key: 'converted' },
    { title: 'Conversion Rate', dataIndex: 'rate', key: 'rate', render: (v: number) => `${v}%` },
    { title: 'Revenue (₹)', dataIndex: 'revenue', key: 'revenue', render: (v: number) => v.toLocaleString('en-IN') },
  ]

  const handleExportExcel = () => {
    const header = 'Month,Total Leads,Converted,Conversion Rate,Revenue (INR)'
    const rows = reportData.map((r) => [r.month, r.totalLeads, r.converted, `${r.rate}%`, r.revenue].join(','))
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `Lead_Conversion_Report_${dayjs().format('YYYY-MM-DD')}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    message.success('Lead conversion report exported')
  }

  const handlePrint = () => {
    const rowsHtml = reportData
      .map((r) => `<tr><td>${r.month}</td><td>${r.totalLeads}</td><td>${r.converted}</td><td>${r.rate}%</td><td>${inr(r.revenue)}</td></tr>`)
      .join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Lead Conversion Report</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:Arial,sans-serif;padding:30px;color:#111;background:#fff}
      h2{color:#001A4D;margin:0 0 4px}
      .sub{font-size:11px;color:#8A93A6;margin-bottom:20px}
      .kpis{display:flex;gap:12px;margin-bottom:24px}
      .kpi{flex:1;border:1px solid #e3e6ee;border-radius:8px;padding:12px 14px}
      .kpi .label{font-size:11px;color:#8A93A6;text-transform:uppercase}
      .kpi .value{font-size:20px;font-weight:700;color:#001A4D;margin-top:4px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}
      th,td{padding:8px 12px;border:1px solid #e3e6ee;text-align:left}
      th{background:#f4f6fa}
      .footer{margin-top:30px;font-size:11px;color:#888;text-align:center;border-top:1px solid #ddd;padding-top:10px}
      @media print{body{padding:0}}
    </style></head><body>
    <h2>Lead Conversion Report</h2>
    <div class="sub">Shakthi Power &middot; Generated on ${dayjs().format('DD MMM YYYY')}</div>
    <div class="kpis">
      <div class="kpi"><div class="label">Avg Conversion Rate</div><div class="value">${avgConversionRate}%</div></div>
      <div class="kpi"><div class="label">Total Conversions</div><div class="value">${totalConversions}</div></div>
      <div class="kpi"><div class="label">Total Revenue</div><div class="value">${inr(totalRevenue)}</div></div>
      <div class="kpi"><div class="label">Avg Revenue/Lead</div><div class="value">${inr(avgRevenuePerLead)}</div></div>
    </div>
    <table>
      <thead><tr><th>Month</th><th>Total Leads</th><th>Converted</th><th>Conversion Rate</th><th>Revenue (₹)</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="footer">Shakthi Power ERP &middot; This is a computer-generated report</div>
    </body></html>`
    const printWindow = window.open('', '_blank')
    if (!printWindow) return
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => printWindow.print(), 500)
  }

  return (
    <div className="lead-list-container">
      <div className="list-header">
        <h2>Lead Conversion Report</h2>
        <Space>
          {canDownload && <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>Export Excel</Button>}
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="kpi-card">
            <Statistic
              title="Average Conversion Rate"
              value={avgConversionRate}
              suffix="%"
              prefix={avgConversionRate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="kpi-card">
            <Statistic
              title="Total Conversions"
              value={totalConversions}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="kpi-card">
            <Statistic
              title="Total Revenue"
              value={totalRevenue}
              prefix="₹"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="kpi-card">
            <Statistic
              title="Avg Revenue/Lead"
              value={avgRevenuePerLead}
              prefix="₹"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: '16px' }}>
        <h3>Monthly Breakdown</h3>
        <Table
          dataSource={reportData}
          columns={columns}
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true }}
          rowKey="key"
        />
      </Card>
    </div>
  )
}

export default LeadConversionReport
